import React, { useEffect, useState } from 'react'
import { useTheme } from '@mui/material/styles'
import {
    ListVendor,
    PricingContracts,
    PricingContractStatus,
    VendorServiceContract,
} from '../../../../models'
import { Container, EzosLoader } from '../../../../components'
import { Button, Grid, Tooltip, Typography, IconButton } from '@mui/material'
import SendIcon from '@material-ui/icons/Send'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Paper from '@mui/material/Paper'
import Divider from '@mui/material/Divider'
import { toMMDDYYYY } from '../../../../helpers/date'
import PictureAsPdfIcon from '@material-ui/icons/PictureAsPdf'
import { DisplayState } from './PricingTab'

interface Props {
    vendor: ListVendor
    onClickRequestUpdatedPricing: (vendor: ListVendor) => void
    pricingContracts: PricingContracts[]
    displayState: DisplayState
    setDisplayState: (displayState: DisplayState) => void
    handleGeneratePricingContractPDF: () => void
}

export const VendorPricing = (props: Props) => {
    const {
        vendor,
        onClickRequestUpdatedPricing,
        pricingContracts,
        displayState,
        setDisplayState,
        handleGeneratePricingContractPDF,
    } = props
    const theme = useTheme()

    console.log('pricingContracts', pricingContracts)

    const currentPricingContracts: PricingContracts[] = pricingContracts.filter(
        (pc: PricingContracts) => pc.status === PricingContractStatus.CURRENT,
    )

    console.log('currentPricingContract', currentPricingContracts)

    const contracts: VendorServiceContract[] = []

    currentPricingContracts.forEach((pc: PricingContracts) => {
        contracts.push(...pc.service_contracts)
    })
    const unitContracts = contracts.filter((c) => !!c.unit_name)
    const areaContracts = contracts.filter((c) => !!c.area_name)

    const awaitingVendorPricingContract = pricingContracts.find(
        (pc: PricingContracts) =>
            pc.status === PricingContractStatus.AWAITING_VENDOR,
    )

    const requestedDate =
        awaitingVendorPricingContract?.created_date || new Date().toISOString()

    return (
        <Container
            style={{
                flexDirection: 'column',
                alignItems: 'flex-start',
                padding: theme.spacing(2),
            }}
        >
            {/* header */}
            <Container
                style={{
                    flexDirection: 'row',
                    alignItems: 'flex-start',
                    gap: theme.spacing(1),
                    marginBottom: theme.spacing(2),
                    width: '100%',
                }}
            >
                {/* Update Pricing Button */}
                {/* If displayState.requestVendorPricing is true, then show the last updated date
                    and the ability to request updated pricing
                    If displayState.awaitingVendor is true, then show the date requested
                    */}
                {displayState.requestVendorPricing && (
                    <Container
                        style={{
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            gap: theme.spacing(1),
                            width: '100%',
                        }}
                    >
                        <Typography variant="h5">{vendor.name}</Typography>
                        <Container
                            style={{
                                marginLeft: theme.spacing(3),
                                flexDirection: 'row',
                                gap: theme.spacing(1),
                            }}
                        >
                            <Typography variant="body1">
                                Last updated:{' '}
                            </Typography>
                            <Typography variant="body1" fontWeight={600}>
                                {(() => {
                                    const modifiedDate =
                                        currentPricingContracts[0]
                                            ?.modified_date
                                    return modifiedDate
                                        ? toMMDDYYYY(new Date(modifiedDate))
                                        : ''
                                })()}
                            </Typography>
                        </Container>
                        <Button
                            onClick={(e) => {
                                e.stopPropagation()
                                onClickRequestUpdatedPricing(vendor)
                                setDisplayState({
                                    ...displayState,
                                    requestVendorPricing: false,
                                    awaitingVendor: true,
                                })
                            }}
                            variant="contained"
                            color="primary"
                            style={{ marginTop: theme.spacing(1) }}
                        >
                            <SendIcon style={{ fontSize: '17px' }} />
                            <Container
                                style={{
                                    margin: theme.spacing(0, 3, 0, 1),
                                    fontSize: '14px',
                                }}
                            >
                                Request Updated Pricing
                            </Container>
                        </Button>
                    </Container>
                )}
                {displayState.awaitingVendor && (
                    <Container
                        style={{
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            gap: theme.spacing(1),
                            width: '100%',
                        }}
                    >
                        <Typography variant="h5">{vendor.name}</Typography>
                        <Container
                            style={{
                                marginLeft: theme.spacing(3),
                                flexDirection: 'row',
                                gap: theme.spacing(1),
                                width: '100%',
                            }}
                        >
                            <Typography variant="body1">
                                Updated Pricing Requested On:{' '}
                            </Typography>
                            <Typography variant="body1" fontWeight={600}>
                                {toMMDDYYYY(new Date(requestedDate))}
                            </Typography>
                        </Container>
                    </Container>
                )}

                {/* PDF Button */}
                <Container
                    style={{
                        flexDirection: 'column',
                        alignItems: 'flex-end',
                        justifyContent: 'flex-end',
                        gap: theme.spacing(1),
                    }}
                >
                    <Tooltip title="Download PDF">
                        <IconButton onClick={handleGeneratePricingContractPDF}>
                            <PictureAsPdfIcon />
                        </IconButton>
                    </Tooltip>
                </Container>
            </Container>

            <Divider style={{ margin: theme.spacing(2, 0) }} />
            {/* Pricing Matrix */}
            <Paper
                style={{
                    width: '100%',
                    padding: theme.spacing(2),
                    boxSizing: 'border-box',
                }}
            >
                <Grid
                    container
                    spacing={2}
                    justifyContent="center"
                    alignItems="flex-start"
                    style={{ width: '100%' }}
                >
                    {areaContracts.length > 0 && (
                        <Grid
                            item
                            xs={12}
                            md={6}
                            style={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                            }}
                        >
                            <div style={{ maxWidth: 500, width: '100%' }}>
                                <Typography variant="h6" gutterBottom>
                                    Area Pricing (Partial)
                                </Typography>
                                <VendorPricingMatrix
                                    contracts={areaContracts}
                                    isUnit={false}
                                />
                            </div>
                        </Grid>
                    )}
                    {unitContracts.length > 0 && (
                        <Grid item xs={12} md={6}>
                            <div style={{ maxWidth: 500, width: '100%' }}>
                                <Typography variant="h6" gutterBottom>
                                    Unit Pricing (Whole Unit)
                                </Typography>
                                <VendorPricingMatrix
                                    contracts={unitContracts}
                                    isUnit={true}
                                />
                            </div>
                        </Grid>
                    )}
                </Grid>
            </Paper>
        </Container>
    )
}

interface VendorPricingMatrixProps {
    contracts: VendorServiceContract[]
    isUnit: boolean
}

const VendorPricingMatrix: React.FC<VendorPricingMatrixProps> = ({
    contracts,
    isUnit,
}) => {
    // Get all unique service names (columns)
    const serviceNames = Array.from(
        new Set(contracts.map((c) => c.service_name)),
    )
    // Get all unique unit/area names (rows)
    const unitAreaNames = Array.from(
        new Set(
            contracts
                .map((c) => c.unit_name || c.area_name)
                .filter((v): v is string => Boolean(v)),
        ),
    )

    // Build a lookup: { [unitAreaName]: { [serviceName]: price } }
    const priceLookup: Record<string, Record<string, string>> = {}
    contracts.forEach((c) => {
        const row = c.unit_name || c.area_name
        if (!row) return
        if (!priceLookup[row]) priceLookup[row] = {}
        priceLookup[row][c.service_name] = c.price
    })

    console.log(serviceNames)
    console.log(contracts)

    return (
        <TableContainer component={Paper}>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell>
                            <b>{isUnit ? 'Unit' : 'Area'} Name</b>
                        </TableCell>
                        {serviceNames.map((service) => (
                            <TableCell key={service}>
                                <b>{service}</b>
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {unitAreaNames.map((unitArea) => (
                        <TableRow key={unitArea}>
                            <TableCell>{unitArea}</TableCell>
                            {serviceNames.map((service) => (
                                <TableCell key={service}>
                                    {priceLookup[unitArea]?.[service] ?? '-'}
                                </TableCell>
                            ))}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    )
}
